import { useQuery } from "@tanstack/react-query";
import { message } from "antd";
import { type IListPageChildProps } from "layouts/ListPageLayout";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";
import { ScreenMax } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import { BTDropdown, BTDropdownButton } from "@btnet/core-components";
import { useWebviewContext } from "@btnet/utilities-webview";

import {
    BdsIconCaretSmallDown,
    BdsIconPlus,
    BdsIconShare,
    BdsLayout,
} from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { usePermission } from "helpers/globalContext/usePermission";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { routes } from "utilities/routes";

import BdsListPageHeader from "commonComponents/bdsWrappers/BdsListPageHeader/BdsListPageHeader";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTListHeader } from "commonComponents/btWrappers/BTListHeader/BTListHeader";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { type ExportHeaderResponse } from "commonComponents/btWrappers/BTSocialMediaButtons/FileShare.types";
import { Frame } from "commonComponents/btWrappers/Frame/Frame";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { withJobsitesRequired } from "commonComponents/helpers/JobsitesRequired/JobsitesRequired";
import { withSingleBuilderRequired } from "commonComponents/helpers/SingleBuilderRequired/SingleBuilderRequired";
import { openBackgroundProcessStatusPopup } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup";
import { ProcessStatusGroupType } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup.types";

import { type PlanPage, type PlanSet, type PlansShareResponse } from "handlers";

import {
    plansExternalByShareTokenGetV2,
    plansGetV2,
    plansSharePostV2,
    usePlansByPageIdDeleteV2,
    usePlansReorderSetsPutV2,
    usePlansSetBySetIdDeleteV2,
} from "handlers/plans/plans";

import { PlanPagesList } from "entity/Plans/PlanPagesList/PlanPagesList";
import { PlansEmptyState } from "entity/Plans/PlansEmptyState/PlansEmptyState";
import { PlanSetsPanel } from "entity/Plans/PlanSets/PlanSetsPanel";
import { PlansOrganizer } from "entity/Plans/PlansOrganizer/PlansOrganizer";
import { useSharedContent } from "entity/sharedContent/useSharedContent";
import { SpecsPlansViewToggle } from "entity/specification/components/SpecsPlansViewToggle";

import "./PlansList.less";

const PlansUploadModal = lazyLoadComponent(() =>
    import("entity/Plans/PlansUploadModal/PlansUploadModal").then((m) => ({
        default: m.PlansUploadModal,
    })),
);
const GenerateQRCodeModal = lazyLoadComponent(
    () => import("commonComponents/entity/sharing/GenerateQRCodeModal"),
);

type IPlansListProps = IListPageChildProps;

/**
 * History state for scroll position preservation when navigating back from plan viewer
 */
interface IPlansListHistoryState {
    lastViewedPageId?: string;
}

export enum SharePlanType {
    PLANS = 0,
    SET = 1,
    PAGE = 2,
}

export function getPlansListQueryKey(jobId: number | undefined) {
    return ["plans", jobId];
}

function PlansListInternal(props: Readonly<IPlansListProps>) {
    const { jobIds, jobName, setJobPickerCollapsed } = props;
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadModalTargetSetId, setUploadModalTargetSetId] = useState<string | undefined>();
    const [isOrganizing, setIsOrganizing] = useState(false);
    const [showGenerateQRCodeModal, setShowGenerateQRCodeModal] = useState<boolean>(false);
    const [shareData, setShareData] = useState<PlansShareResponse | null>(null);
    const [planPageTitle, setPlanPageTitle] = useState<string | null>(null);
    const { shareToken } = useSharedContent();
    const canAdd = usePermission("specifications", "canAdd");
    const userInfo = useContext(UserInfoContext);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const webviewContext = useWebviewContext();

    const { setId } = useParams<{ setId?: string }>();

    const deletePlanPageMutation = usePlansByPageIdDeleteV2();
    const deletePlanSetMutation = usePlansSetBySetIdDeleteV2();
    const { mutateAsync: reorderPlanSets } = usePlansReorderSetsPutV2();

    // Determine layout mode based ONLY on screen size (for both webview and normal web)
    // Note: ScreenMax.md = 991px (matches CSS media query at 992px)
    const useMobileLayout = useMediaQuery(`(max-width: ${ScreenMax.md}px)`);
    const scrollablePanelRef = useRef<HTMLDivElement>(null);
    const isChangingSetRef = useRef<boolean>(false);

    const scrollPositionsRef = useRef<Record<string, number>>({});

    const location = useLocation<IPlansListHistoryState>();
    const lastViewedPageIdRef = useRef(location.state?.lastViewedPageId);
    const builderInfo = useContext(BuilderInfoContext);

    const authenticated = useQuery({
        queryKey: getPlansListQueryKey(jobIds[0]),
        queryFn: () => {
            return plansGetV2({ jobId: jobIds[0] });
        },
        enabled: !shareToken && jobIds.length > 0 && !!jobIds[0],
        retry: false,
    });

    const external = useQuery({
        queryKey: [shareToken],
        queryFn: () => {
            return plansExternalByShareTokenGetV2(shareToken!);
        },
        enabled: !!shareToken,
    });

    const { data: plansData, isLoading, refetch, error } = shareToken ? external : authenticated;

    const hasError = !!error;

    if (hasError) {
        // throw error to be caught by error boundary
        throw error as Error;
    }

    // Determine empty state and loading state
    const hasPlans = plansData?.data?.planSets && plansData.data.planSets.length > 0;
    const showEmptyState = !isLoading && !hasPlans;

    const navigateToPlans = useCallback(
        (planSetId?: string) => {
            if (shareToken) {
                navigate(routes.plans.getExternalLink(shareToken, planSetId));
            } else {
                navigate(routes.plans.getPlansListLink(planSetId));
            }
        },
        [navigate, shareToken],
    );

    const planSets = plansData?.data?.planSets;
    const selectedPlanSet = useMemo(() => {
        if (!setId || !planSets) return undefined;
        return planSets.find((s) => s.setId === setId);
    }, [setId, planSets]);

    const selectedPlanPages = useMemo(
        () =>
            (plansData?.data.planPages ?? []).filter(
                (plan: PlanPage) => plan.setId === selectedPlanSet?.setId,
            ),
        [plansData?.data.planPages, selectedPlanSet?.setId],
    );

    // Track when to skip the "deleted" warning (job change or user-initiated deletion)
    const previousJobIdRef = useRef<number | undefined>(jobIds[0]);
    const skipDeletedWarningRef = useRef(false);

    const handlePlanClick = useCallback(
        (plan: PlanPage) => {
            if (shareToken) {
                navigate(routes.plans.getExternalViewerLink(shareToken, plan.pageId));
            } else if (plan.pageId) {
                navigate(routes.plans.getViewerLink(plan.pageId));
            }
        },
        [navigate, shareToken],
    );

    useEffect(() => {
        setIsOrganizing(false);
        if (!useMobileLayout) {
            setJobPickerCollapsed(false);
        }
        const currentJobId = jobIds[0];
        if (previousJobIdRef.current !== undefined && previousJobIdRef.current !== currentJobId) {
            skipDeletedWarningRef.current = true;
            scrollPositionsRef.current = {};
        }
        previousJobIdRef.current = currentJobId;
    }, [jobIds, setJobPickerCollapsed, useMobileLayout]);

    const handleOrganize = useCallback(() => {
        setIsOrganizing(true);
        if (!useMobileLayout) {
            setJobPickerCollapsed(true);
        }
    }, [setJobPickerCollapsed, useMobileLayout]);

    const organizeHandled = useRef(false);

    useEffect(() => {
        if (!organizeHandled.current) {
            setIsOrganizing(false);
            if (!useMobileLayout) {
                setJobPickerCollapsed(false);
            }
        }
        organizeHandled.current = false;
    }, [location.key, setJobPickerCollapsed, useMobileLayout]);

    useEffect(() => {
        if (
            searchParams.get("organize") === "true" &&
            selectedPlanSet &&
            !organizeHandled.current
        ) {
            organizeHandled.current = true;
            handleOrganize();
            setSearchParams(
                (prev) => {
                    prev.delete("organize");
                    return prev;
                },
                { replace: true },
            );
        }
    }, [searchParams, selectedPlanSet, handleOrganize, setSearchParams]);

    const isPageReload = useRef(
        (performance.getEntriesByType("navigation") as PerformanceNavigationTiming[])[0]?.type ===
            "reload",
    );
    const hasAutoNavigatedToSetRef = useRef(false);

    useEffect(() => {
        if (isLoading || !planSets) return;
        if (planSets.length === 0) {
            if (setId) {
                skipDeletedWarningRef.current = false;
                navigateToPlans();
            }
            return;
        }

        // If URL has setId, validate it exists in current data
        if (setId) {
            const setExists = planSets.some((s) => s.setId === setId);
            if (!setExists) {
                if (!skipDeletedWarningRef.current) {
                    void message.warning(
                        "This plan has been deleted or your permissions have changed.",
                    );
                }
                skipDeletedWarningRef.current = false;
                navigateToPlans(planSets[0].setId);
            }
            return;
        }

        skipDeletedWarningRef.current = false;

        // On external (share link) pages, honor the server-provided setId on the
        // initial load only. A browser reload preserves the user's current setId
        // in the URL, so this branch only fires when no setId is in the URL yet.
        if (
            shareToken &&
            plansData?.data?.setId &&
            !hasAutoNavigatedToSetRef.current &&
            !isPageReload.current
        ) {
            hasAutoNavigatedToSetRef.current = true;
            navigateToPlans(plansData.data.setId);
            return;
        }

        if (!useMobileLayout && planSets.length > 0) {
            navigateToPlans(planSets[0].setId);
        }
    }, [
        isLoading,
        planSets,
        setId,
        useMobileLayout,
        navigateToPlans,
        plansData?.data?.setId,
        shareToken,
    ]);

    const hasNavigatedToPageRef = useRef(false);
    useEffect(() => {
        if (hasNavigatedToPageRef.current || isPageReload.current) return;
        if (!isLoading && !lastViewedPageIdRef.current && plansData?.data?.pageId) {
            const planPageToView = plansData?.data?.planPages.find(
                (page) => plansData.data.pageId === page.pageId,
            );
            if (planPageToView) {
                hasNavigatedToPageRef.current = true;
                handlePlanClick(planPageToView);
            }
        }
    }, [handlePlanClick, plansData?.data?.pageId, plansData?.data?.planPages, isLoading]);

    // Scroll to last viewed plan card when returning from viewer
    useEffect(() => {
        if (!isLoading && lastViewedPageIdRef.current) {
            requestAnimationFrame(() => {
                const cardElement = scrollablePanelRef.current?.querySelector(
                    `[data-testid="plans-card-${lastViewedPageIdRef.current!}"]`,
                );
                if (cardElement) {
                    cardElement.scrollIntoView({ behavior: "instant", block: "center" });
                }
            });
        }
    }, [isLoading]);

    // Restore scroll position when switching between plan sets
    useEffect(() => {
        if (!isLoading && setId && isChangingSetRef.current) {
            const savedPosition = scrollPositionsRef.current[setId];
            requestAnimationFrame(() => {
                scrollablePanelRef.current?.scrollTo(0, savedPosition ?? 0);
            });
        }
    }, [setId, isLoading]);

    // Track mobile panel visibility based on URL
    const showMobilePanel = useMobileLayout && !setId;

    const handleUploadClick = () => {
        setUploadModalTargetSetId(undefined);
        setIsUploadModalOpen(true);
    };

    const handleUploadRevision = useCallback((planSet: PlanSet) => {
        setUploadModalTargetSetId(planSet.setId);
        setIsUploadModalOpen(true);
    }, []);

    const handleUploadModalClose = () => {
        setIsUploadModalOpen(false);
        setUploadModalTargetSetId(undefined);
    };

    const modalConfig = {
        parentRoute: routes.plans.getPlansListLink(setId),
        beforeClose: handleUploadModalClose,
    };

    const handleUploadComplete = async (backgroundJobIds: string[]) => {
        // Filter out empty backgroundJobIds (files that completed synchronously)
        const asyncJobIds = backgroundJobIds.filter((id) => id && id.length > 0);

        // Show background job status popup only if there are async jobs
        if (asyncJobIds.length > 0) {
            // Open status popup for the first job (existing API only supports single job ID)
            openBackgroundProcessStatusPopup(ProcessStatusGroupType.PlanUpload);

            // Show success message
            message.success("Files uploaded, processing in the background", 6);
        } else if (backgroundJobIds.length > 0) {
            // All files completed synchronously
            message.success("Files were uploaded successfully");
        }

        await refetch();
    };

    const handleUploadError = (error: Error) => {
        showApixErrorMessage(error, "Error uploading plan files.");
    };

    // For the empty-state first-upload flow: the inline circle loader already waited for BG
    // processing to complete, so we just need to refetch — no popup or "still processing" toast.
    const handlePlansReady = async () => {
        await refetch();
    };

    const handleCopyShareLink = useCallback(
        async (id?: string, shareType?: SharePlanType) => {
            // Safari/iOS require navigator.clipboard.write to run inside the
            // user-gesture frame — awaiting the network call before writeText
            // consumes the activation and the write is rejected. Passing a
            // ClipboardItem with a Promise<Blob> lets the request resolve while
            // activation is still valid.
            const sharePromise = plansSharePostV2({
                jobId: jobIds[0],
                pageId: shareType === SharePlanType.PAGE ? id : undefined,
                setId: shareType === SharePlanType.SET ? id : undefined,
            });
            try {
                const clipboardItem = new ClipboardItem({
                    "text/plain": sharePromise.then(
                        (resp) => new Blob([resp.data.shareableLink ?? ""], { type: "text/plain" }),
                    ),
                });
                await navigator.clipboard.write([clipboardItem]);
                const resp = await sharePromise;
                if (resp.data.shareableLink) {
                    void message.success("Share link copied");
                }
            } catch {
                const resp = await sharePromise;
                if (resp.data.shareableLink) {
                    await navigator.clipboard.writeText(resp.data.shareableLink);
                    void message.success("Share link copied");
                }
            }
        },
        [jobIds],
    );

    const handleGenerateQRCode = useCallback(
        async (shareType: SharePlanType, id?: string, qrCodeTitle?: string) => {
            const resp = await plansSharePostV2({
                jobId: jobIds[0],
                pageId: shareType === SharePlanType.PAGE ? id : undefined,
                setId: shareType === SharePlanType.SET ? id : undefined,
            });
            if (resp.data.shareableLink) {
                if (qrCodeTitle) {
                    setPlanPageTitle(qrCodeTitle);
                }
                setShareData(resp.data);
                setShowGenerateQRCodeModal(true);
            }
        },
        [jobIds],
    );

    const handlePlanSetClick = (planSet: PlanSet) => {
        isChangingSetRef.current = true;
        if (setId && scrollablePanelRef.current) {
            scrollPositionsRef.current[setId] = scrollablePanelRef.current.scrollTop;
        }
        navigateToPlans(planSet.setId);
    };

    const handleDeletePlanSet = async (setId: string) => {
        await deletePlanSetMutation.mutateAsync({ setId, params: { jobId: jobIds[0] } });
        message.success("Plan deleted successfully");
        skipDeletedWarningRef.current = true;
        await refetch();
    };

    const handleReorderPlanSets = useCallback(
        async (setIdsInOrder: string[]) => {
            try {
                await reorderPlanSets({
                    data: { setIdsInOrder },
                    params: { jobId: jobIds[0] },
                });
                await refetch();
            } catch (error) {
                showApixErrorMessage(error as Error, "Failed to reorder plan sets");
                await refetch();
                throw error;
            }
        },
        [reorderPlanSets, jobIds, refetch],
    );

    const canReorder = !shareToken && userInfo?.loginType === BTLoginTypes.BUILDER;
    const reorderHandler = canReorder ? handleReorderPlanSets : undefined;

    const handleDeletePlanPage = async (pageId: string) => {
        await deletePlanPageMutation.mutateAsync({ pageId, params: { jobId: jobIds[0] } });
        message.success("Page deleted successfully");
        await refetch();
    };

    const handleBackToList = () => {
        navigateToPlans();
    };

    const handleViewVersionHistory = useCallback(
        (plan: PlanPage) => {
            navigate(routes.plans.getViewerLink(plan.pageId) + "?versionHistory=true");
        },
        [navigate],
    );

    const handleUploadNewVersion = useCallback(
        (plan: PlanPage) => {
            navigate(routes.plans.getViewerLink(plan.pageId) + "?uploadVersion=true");
        },
        [navigate],
    );

    const handleOrganizeSave = useCallback(async () => {
        setIsOrganizing(false);
        if (!useMobileLayout) {
            setJobPickerCollapsed(false);
        }
        await refetch();
    }, [useMobileLayout, refetch, setJobPickerCollapsed]);

    const handleOrganizeCancel = useCallback(() => {
        setIsOrganizing(false);
        if (!useMobileLayout) {
            setJobPickerCollapsed(false);
        }
    }, [setJobPickerCollapsed, useMobileLayout]);

    // Gates the notify recipient pickers (upload modal + revision review). With the flag off, new-set
    // uploads offer no notify option and applying revisions commits immediately without the notify modal.
    const notificationsEnabled = !!builderInfo?.flags?.plansNotifications;

    const shareButton =
        userInfo?.loginType === BTLoginTypes.BUILDER ? (
            <BTDropdownButton
                data-testid="planSetShareButtonGroup"
                type="tertiary"
                showCaret={false}
                buttonProps={{
                    icon: <BdsIconShare />,
                    "aria-label": "Share plans dropdown",
                    className: "CompactShare ShareSection",
                }}
                menuItems={[
                    {
                        "data-testid": "planSetShareLink",
                        content: "Copy share link",
                        onClick: async () => handleCopyShareLink(),
                        "aria-label": "Copy plan set share link",
                    },
                    {
                        "data-testid": "planSetGenerateQrCode",
                        content: "Create QR code",
                        onClick: () => handleGenerateQRCode(SharePlanType.PLANS),
                        "aria-label": "Create plan set QR code",
                    },
                ]}
            />
        ) : (
            <BTButton
                data-testid="planSetShareLink"
                icon={<BdsIconShare />}
                aria-label="Copy plan set share link"
                className="CompactShare ShareSection"
                type="tertiary"
                onClick={async () => handleCopyShareLink()}
            />
        );

    const BTShareButton =
        userInfo?.loginType === BTLoginTypes.BUILDER ? (
            <BTDropdown
                testid="planSetShareButtonGroup-dropdown"
                overlay={
                    <BTMenu testid="planSetShareButtonGroup-menu">
                        <BTMenu.Item
                            testid="planSetShareLink"
                            aria-label="Copy plan set share link"
                            onClick={async () => handleCopyShareLink()}
                        >
                            Copy share link
                        </BTMenu.Item>
                        <BTMenu.Item
                            testid="planSetGenerateQrCode"
                            aria-label="Create plan set QR code"
                            onClick={() => handleGenerateQRCode(SharePlanType.PLANS)}
                        >
                            Create QR code
                        </BTMenu.Item>
                    </BTMenu>
                }
            >
                <BTButton
                    icon={<BdsIconShare />}
                    aria-label="Share plans dropdown"
                    type="secondary"
                    data-testid="planSetShareButtonGroup"
                >
                    <BdsIconCaretSmallDown />
                </BTButton>
            </BTDropdown>
        ) : (
            <BTButton
                data-testid="planSetShareLink"
                icon={<BdsIconShare />}
                aria-label="Copy plan set share link"
                type="secondary"
                onClick={async () => handleCopyShareLink()}
            >
                Share
            </BTButton>
        );

    const generateQrCodeModalConfig = {
        beforeClose: () => {
            setPlanPageTitle(null);
            setShowGenerateQRCodeModal(false);
        },
        parentRoute: location.pathname,
    };

    const qrCodeModal = (
        <GenerateQRCodeModal
            exportHeader={shareData?.exportHeaderResponse as ExportHeaderResponse}
            shareUrl={shareData?.shareableLink}
            title={planPageTitle ?? "Plans"}
            modalConfig={generateQrCodeModalConfig}
            bottomDescriptionMarkup={
                <>
                    Use your phone's camera to open in Buildertrend
                    <br />
                    Anyone can view this file
                </>
            }
        />
    );
    const showFeedback = userInfo?.loginType === BTLoginTypes.BUILDER && !shareToken && !!hasPlans;

    if (isOrganizing && selectedPlanSet) {
        return (
            <PlansOrganizer
                planPages={selectedPlanPages}
                planSet={selectedPlanSet}
                jobId={jobIds[0]}
                jobName={jobName}
                onSave={handleOrganizeSave}
                onCancel={handleOrganizeCancel}
                onDeletePage={handleDeletePlanPage}
                refetch={refetch}
            />
        );
    }

    const isInWebview = !!webviewContext;
    const showViewToggle = !isInWebview && !shareToken;
    const title = isInWebview ? "Plans" : "Plans and Specs";

    const headerIconActions = [
        {
            icon: "help" as const,
            onClick: () => {
                window.open(
                    "https://helpcenter.buildertrend.net/s/article/Specifications-Overview",
                    "_blank",
                    "noopener,noreferrer",
                );
            },
            isHidden: userInfo?.loginType !== BTLoginTypes.BUILDER || !!shareToken,
        },
    ];

    // Mobile layout: Only render the active panel to avoid hidden DOM nodes
    let content: React.ReactNode;
    if (useMobileLayout) {
        content = (
            <>
                {showMobilePanel ? (
                    <BdsLayout
                        testid="PlanListLayout"
                        className="plans-layout mobile-layout"
                        displayType="HCF"
                    >
                        {builderInfo?.flags?.bdsContentHeaderListPageReplacement && (
                            <BTListHeader
                                testid="PlansContentHeader"
                                className="plans-content-header"
                                ariaLabel="Plans and Specs"
                            >
                                <div className="flex flex-col gap-3">
                                    <div>
                                        <BTListHeader.ActionButtons
                                            testid="PlansContentHeaderActionButtons"
                                            jobName={jobName}
                                            primaryAction={
                                                canAdd && (
                                                    <BTButton
                                                        data-testid="uploadPlan"
                                                        onClick={handleUploadClick}
                                                        loading={isLoading}
                                                        icon={<BdsIconPlus />}
                                                        collapse
                                                    >
                                                        Plan
                                                    </BTButton>
                                                )
                                            }
                                            secondaryActions={
                                                shareToken ? undefined : [BTShareButton]
                                            }
                                            iconActions={headerIconActions}
                                        />
                                        <BTListHeader.Title>{title}</BTListHeader.Title>
                                    </div>
                                    {showViewToggle ? (
                                        <SpecsPlansViewToggle
                                            viewMode={"plans"}
                                            showFeedback={showFeedback}
                                        />
                                    ) : undefined}
                                </div>
                            </BTListHeader>
                        )}
                        {!builderInfo?.flags?.bdsContentHeaderListPageReplacement && (
                            <BdsListPageHeader
                                id="PlansContentHeader"
                                testid="PlansContentHeader"
                                jobName={props.jobName}
                                iconActions={headerIconActions}
                                primaryButtonProps={{
                                    id: "uploadPlan",
                                    text: "Plan",
                                    onClick: handleUploadClick,
                                    isHidden: !canAdd,
                                    loading: isLoading,
                                    disabled: false,
                                }}
                                title={title}
                                bottom={
                                    showViewToggle ? (
                                        <SpecsPlansViewToggle
                                            viewMode={"plans"}
                                            showFeedback={showFeedback}
                                        />
                                    ) : undefined
                                }
                                secondaryButtons={shareToken ? undefined : shareButton}
                            />
                        )}
                        <BdsLayout testid="PlansContent" className="plans-content" displayType="1">
                            {showEmptyState ? (
                                <div className="PlansEmptyStateContainer">
                                    <PlansEmptyState
                                        jobId={jobIds[0]}
                                        canUpload={canAdd && !shareToken}
                                        onPlansReady={handlePlansReady}
                                        onError={handleUploadError}
                                    />
                                </div>
                            ) : (
                                <div className="plans">
                                    <PlanSetsPanel
                                        planSets={plansData?.data?.planSets ?? []}
                                        selectedPlanSetId={selectedPlanSet?.setId}
                                        onSelectPlanSet={handlePlanSetClick}
                                        isLoading={isLoading}
                                        onReorder={reorderHandler}
                                        useMobileLayout={useMobileLayout}
                                    />
                                </div>
                            )}
                        </BdsLayout>
                    </BdsLayout>
                ) : (
                    <BdsLayout
                        testid="PlanPagesListLayout"
                        className="plans-layout mobile-layout"
                        displayType="HCF"
                    >
                        <div className="plans-content">
                            <PlanPagesList
                                planPages={selectedPlanPages}
                                shareToken={shareToken}
                                jobId={jobIds[0]}
                                refetch={refetch}
                                planSet={selectedPlanSet ?? ({} as PlanSet)}
                                isLoading={isLoading}
                                onBack={handleBackToList}
                                useMobileLayout
                                displayJobName={jobName}
                                onHandlePlanClick={handlePlanClick}
                                onDeleteSet={handleDeletePlanSet}
                                onDeletePage={handleDeletePlanPage}
                                scrollablePanelRef={scrollablePanelRef}
                                onOrganize={handleOrganize}
                                onUploadRevision={handleUploadRevision}
                                onCopyShareLink={handleCopyShareLink}
                                onGenerateQrCodeLink={handleGenerateQRCode}
                                onViewVersionHistory={handleViewVersionHistory}
                                onUploadNewVersion={handleUploadNewVersion}
                            />
                        </div>
                    </BdsLayout>
                )}
                <PlansUploadModal
                    isOpen={isUploadModalOpen}
                    onClose={handleUploadModalClose}
                    jobId={jobIds[0]}
                    onUploadComplete={handleUploadComplete}
                    onError={handleUploadError}
                    modalConfig={modalConfig}
                    planSets={plansData?.data?.planSets ?? []}
                    notificationsEnabled={notificationsEnabled}
                    notifyUsers={plansData?.data?.notifyUsers ?? undefined}
                    initialTargetSetId={uploadModalTargetSetId}
                />
                {showGenerateQRCodeModal && qrCodeModal}
            </>
        );
    } else {
        content = (
            <Frame data-testid="PlansList" className="landscape-scroll plans-layout">
                {builderInfo?.flags?.bdsContentHeaderListPageReplacement && (
                    <BTListHeader
                        testid="PlansContentHeader"
                        className="plans-content-header"
                        ariaLabel="Plans and Specs"
                    >
                        <div className="flex flex-col gap-3">
                            <div>
                                <BTListHeader.ActionButtons
                                    jobName={props.jobName}
                                    testid="PlansContentHeaderActionButtons"
                                    primaryAction={
                                        canAdd && (
                                            <BTButton
                                                data-testid="uploadPlan"
                                                onClick={handleUploadClick}
                                                loading={isLoading}
                                                icon={<BdsIconPlus />}
                                                collapse
                                            >
                                                Plan
                                            </BTButton>
                                        )
                                    }
                                    secondaryActions={shareToken ? undefined : [BTShareButton]}
                                    iconActions={headerIconActions}
                                />
                                <BTListHeader.Title>{title}</BTListHeader.Title>
                            </div>
                            {showViewToggle ? (
                                <SpecsPlansViewToggle
                                    viewMode={"plans"}
                                    showFeedback={showFeedback}
                                />
                            ) : undefined}
                        </div>
                    </BTListHeader>
                )}
                {!builderInfo?.flags?.bdsContentHeaderListPageReplacement && (
                    <BdsListPageHeader
                        id="PlansContentHeader"
                        testid="PlansContentHeader"
                        jobName={props.jobName}
                        iconActions={headerIconActions}
                        primaryButtonProps={{
                            id: "uploadPlan",
                            text: "Plan",
                            onClick: handleUploadClick,
                            isHidden: !canAdd,
                            loading: isLoading,
                            disabled: false,
                        }}
                        title={title}
                        bottom={
                            showViewToggle ? (
                                <SpecsPlansViewToggle
                                    viewMode={"plans"}
                                    showFeedback={showFeedback}
                                />
                            ) : undefined
                        }
                        secondaryButtons={shareToken ? undefined : shareButton}
                    />
                )}
                <div data-testid="PlansContent" className="plans-content">
                    {showEmptyState ? (
                        <div className="PlansEmptyStateContainer">
                            <PlansEmptyState
                                jobId={jobIds[0]}
                                canUpload={canAdd && !shareToken}
                                onPlansReady={handlePlansReady}
                                onError={handleUploadError}
                            />
                        </div>
                    ) : (
                        <div className="plans" data-testid="plans">
                            <PlanSetsPanel
                                planSets={plansData?.data?.planSets ?? []}
                                selectedPlanSetId={selectedPlanSet?.setId}
                                onSelectPlanSet={handlePlanSetClick}
                                isLoading={isLoading}
                                onReorder={reorderHandler}
                                useMobileLayout={useMobileLayout}
                            />
                            <div className="plans-panel">
                                <PlanPagesList
                                    planPages={selectedPlanPages}
                                    shareToken={shareToken}
                                    planSet={selectedPlanSet ?? ({} as PlanSet)}
                                    jobId={props.jobIds[0]}
                                    refetch={refetch}
                                    isLoading={isLoading}
                                    onHandlePlanClick={handlePlanClick}
                                    scrollablePanelRef={scrollablePanelRef}
                                    onDeleteSet={handleDeletePlanSet}
                                    onDeletePage={handleDeletePlanPage}
                                    onCopyShareLink={handleCopyShareLink}
                                    onGenerateQrCodeLink={handleGenerateQRCode}
                                    onViewVersionHistory={handleViewVersionHistory}
                                    onUploadNewVersion={handleUploadNewVersion}
                                    onOrganize={handleOrganize}
                                    onUploadRevision={handleUploadRevision}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <PlansUploadModal
                    isOpen={isUploadModalOpen}
                    onClose={handleUploadModalClose}
                    jobId={jobIds[0]}
                    onUploadComplete={handleUploadComplete}
                    onError={handleUploadError}
                    modalConfig={modalConfig}
                    planSets={plansData?.data?.planSets ?? []}
                    notificationsEnabled={notificationsEnabled}
                    notifyUsers={plansData?.data?.notifyUsers ?? undefined}
                    initialTargetSetId={uploadModalTargetSetId}
                />
                {showGenerateQRCodeModal && qrCodeModal}
            </Frame>
        );
    }

    return <>{content}</>;
}

const PlansListWithSingleBuilder = withSingleBuilderRequired("Plans and Specs")(PlansListInternal);
const PlansListWithJobs = withJobsitesRequired("Plans and Specs")(PlansListWithSingleBuilder);
export default withErrorBoundary(PlansListWithJobs)("Could not load Plans and Specs");
export { PlansListInternal as PlansListExternal };
